import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  invoiceApi, paymentApi, timeSlotLabel,
  type InvoiceDetail as InvoiceDetailT, type InvoiceLine,
  type Payment, type InvoiceBillToPart, type NewInvoiceLine,
} from '../api';
import Select from '../components/common/Select';

// Invoice detail page — primary surface for editing a draft invoice and
// reviewing an issued one. Anchors the new line CRUD, recall, credit-note,
// payment list, and email/issue/void actions.

export default function InvoiceDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const [invoice, setInvoice] = useState<InvoiceDetailT | null>(null);
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [billTo, setBillTo] = useState<InvoiceBillToPart[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await invoiceApi.get(id);
      setInvoice(r.invoice);
      setLines(r.lines);
      setPayments(r.payments || []);
      setBillTo(r.bill_to || null);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const isDraft = invoice?.status === 'draft';
  const outstanding = useMemo(() => {
    if (!invoice) return 0;
    return Math.max(0, +(Number(invoice.total_zar) - Number(invoice.amount_paid_zar || 0)).toFixed(2));
  }, [invoice]);

  const issue = async () => {
    if (!invoice) return;
    if (!confirm(`Issue ${invoice.number} as a tax invoice? Once issued, lines can't be edited — only credit-noted.`)) return;
    setBusy('issue');
    try { await invoiceApi.issue(id); await load(); }
    catch (e) { alert((e as Error).message); }
    finally { setBusy(null); }
  };

  const voidIt = async () => {
    if (!invoice) return;
    if (!confirm(`Void ${invoice.number}? Attached time entries and disbursements will be released back to "unbilled".`)) return;
    setBusy('void');
    try {
      const r = await invoiceApi.void(id);
      const released = (r.time_released || 0) + (r.disb_released || 0);
      await load();
      if (released > 0) alert(`Voided. ${r.time_released || 0} time entries + ${r.disb_released || 0} disbursements released back to unbilled.`);
    } catch (e) { alert((e as Error).message); }
    finally { setBusy(null); }
  };

  const recall = async () => {
    if (!invoice) return;
    if (!confirm(`Recall ${invoice.number} back to draft? Allowed only when no payments have been recorded.`)) return;
    setBusy('recall');
    try { await invoiceApi.recall(id); await load(); }
    catch (e) { alert((e as Error).message); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="muted">Loading…</div>;
  if (error || !invoice) return <div className="card" style={{ borderColor: 'var(--color-danger)' }}>Error: {error || 'not found'}</div>;

  return (
    <div className="stack">
      <div className="row">
        <Link to="/invoices" className="muted" style={{ fontSize: 13, textDecoration: 'none' }}>← Invoices</Link>
      </div>

      <div className="row">
        <div className="grow">
          <div className="muted" style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>{invoice.number}</div>
          <h1 style={{ marginBottom: 4 }}>
            {invoice.kind === 'credit_note' ? 'Credit note' :
             invoice.kind === 'proforma'    ? 'Pro-forma invoice' :
             invoice.kind === 'tax_invoice' ? 'Tax invoice' :
             'Fee note'}
          </h1>
          <div className="row" style={{ gap: 12, alignItems: 'baseline' }}>
            <span className={`pill ${statusToneClass(invoice.status)}`}>{invoice.status.replace('_', ' ')}</span>
            <span className="muted" style={{ fontSize: 13 }}>
              {invoice.issue_date ? `Issued ${invoice.issue_date}` : 'Not yet issued'}
              {invoice.due_date  ? ` · Due ${invoice.due_date}`    : ''}
            </span>
          </div>
        </div>
        <a href={invoiceApi.pdfUrl(id)} target="_blank" rel="noopener"><button>PDF</button></a>
        {isDraft && (
          <button className="primary" onClick={issue} disabled={busy !== null}>
            {busy === 'issue' ? '…' : 'Issue'}
          </button>
        )}
        {(invoice.status === 'issued' || invoice.status === 'overdue') && (Number(invoice.amount_paid_zar) || 0) === 0 && (
          <button onClick={recall} disabled={busy !== null} title="Move issued invoice back to draft (no payments allowed)">
            {busy === 'recall' ? '…' : 'Recall to draft'}
          </button>
        )}
        {invoice.status !== 'void' && invoice.status !== 'paid' && invoice.status !== 'draft' && (
          <CreditNoteButton invoice={invoice} lines={lines} onCreated={(cn) => nav(`/invoices/${cn.id}`)} />
        )}
        {invoice.status !== 'void' && invoice.status !== 'paid' && (
          <button onClick={voidIt} disabled={busy !== null}>
            {busy === 'void' ? '…' : 'Void'}
          </button>
        )}
      </div>

      {/* Bill-to + header card */}
      <div className="row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        <div className="card">
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Bill to</div>
          {billTo && billTo.length ? (
            <div style={{ marginTop: 6 }}>
              {billTo.map((b, i) => (
                <div key={i} style={{ fontSize: 14 }}>
                  <strong>{b.name}</strong>
                  {b.email && <div className="muted" style={{ fontSize: 12 }}>{b.email}</div>}
                  {b.kind === 'firm' && b.vat_number && (
                    <div className="muted" style={{ fontSize: 12 }}>VAT: {b.vat_number}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>No billed attorney / firm on record.</div>
          )}
        </div>
        <Metric label="Subtotal"  value={formatZar(invoice.subtotal_zar)} />
        <Metric label="VAT"       value={formatZar(invoice.vat_zar)} />
      </div>

      <div className="row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        <Metric label="Total"        value={formatZar(invoice.total_zar)} accent />
        <Metric label="Paid"         value={formatZar(invoice.amount_paid_zar)} />
        <Metric label="Outstanding"  value={formatZar(outstanding)} danger={outstanding > 0} />
      </div>

      {/* Header edit form (drafts only) */}
      {isDraft && <DraftHeaderEditor invoice={invoice} onSaved={load} />}

      {/* Lines table */}
      <LinesPanel
        invoiceId={id}
        editable={isDraft}
        lines={lines}
        currency={invoice.currency || 'ZAR'}
        onChange={load}
      />

      {/* Payments list */}
      {payments.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={sectionHeader}>
            <h4 style={{ margin: 0 }}>Payments ({payments.length})</h4>
          </div>
          <table>
            <thead>
              <tr>
                <th>Received</th><th>Method</th><th>Reference</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ width: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={String(p.ROWID)}>
                  <td style={{ whiteSpace: 'nowrap' }}>{p.received_on}</td>
                  <td><span className="pill">{p.method.replace('_', ' ')}</span></td>
                  <td>{p.reference || <span className="muted">—</span>}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatZar(p.amount_zar)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="ghost"
                      style={{ padding: '4px 8px', color: 'var(--color-danger)' }}
                      onClick={async () => {
                        if (!confirm('Delete this payment? The invoice balance will be restored.')) return;
                        await paymentApi.remove(p.ROWID as number);
                        load();
                      }}
                    >×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {invoice.notes && (
        <div className="card">
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Notes</div>
          <div style={{ whiteSpace: 'pre-wrap', marginTop: 6, fontSize: 13 }}>{invoice.notes}</div>
        </div>
      )}
    </div>
  );
}

// ---- Header editor (draft only) --------------------------------------------
function DraftHeaderEditor({ invoice, onSaved }: { invoice: InvoiceDetailT; onSaved: () => void }) {
  const [notes, setNotes] = useState(invoice.notes || '');
  const [dueDate, setDueDate] = useState(invoice.due_date || '');
  const [issueDate, setIssueDate] = useState(invoice.issue_date || '');
  const [saving, setSaving] = useState(false);
  const dirty = notes !== (invoice.notes || '') ||
                dueDate !== (invoice.due_date || '') ||
                issueDate !== (invoice.issue_date || '');

  const save = async () => {
    setSaving(true);
    try {
      await invoiceApi.update(invoice.ROWID, {
        notes: notes || undefined,
        due_date: dueDate || undefined,
        issue_date: issueDate || undefined,
      });
      onSaved();
    } catch (e) { alert((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div className="card">
      <h4 style={{ margin: '0 0 10px' }}>Edit header</h4>
      <div className="row">
        <div style={{ flex: 1 }}>
          <label>Issue date</label>
          <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label>Due date</label>
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label>Notes</label>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes — appears on the PDF footer." />
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="primary" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save header'}
        </button>
      </div>
    </div>
  );
}

// ---- Lines table -----------------------------------------------------------
function LinesPanel({
  invoiceId, editable, lines, currency, onChange,
}: { invoiceId: number | string; editable: boolean; lines: InvoiceLine[]; currency: string; onChange: () => void }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={sectionHeader}>
        <h4 style={{ margin: 0 }}>Lines ({lines.length})</h4>
        {editable && <button className="primary" onClick={() => setAdding(true)}>+ Add manual line</button>}
        {!editable && <span className="muted" style={{ fontSize: 12 }}>Read-only — issue a credit note to adjust.</span>}
      </div>
      {lines.length === 0 ? (
        <div style={{ padding: 18 }} className="muted">No lines on this invoice yet.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th style={{ width: 90 }}>Kind</th>
              <th>Description</th>
              <th style={{ textAlign: 'right', width: 70 }}>Qty</th>
              <th style={{ width: 60 }}>Unit</th>
              <th style={{ textAlign: 'right', width: 110 }}>Rate</th>
              <th style={{ textAlign: 'right', width: 60 }}>VAT %</th>
              <th style={{ textAlign: 'right', width: 110 }}>Subtotal</th>
              <th style={{ textAlign: 'right', width: 110 }}>Total</th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const slot = timeSlotLabel(l);
              return (
              <tr key={String(l.ROWID)}>
                <td><span className="pill">{l.kind.replace('_', ' ')}</span></td>
                <td>
                  <div>{l.description}</div>
                  {slot && (
                    <div className="muted" style={{ fontSize: 12, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                      {slot}
                    </div>
                  )}
                </td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(l.quantity).toFixed(2)}</td>
                <td className="muted" style={{ fontSize: 12 }}>{l.unit}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatZar(l.rate_zar)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(l.tax_percent).toFixed(0)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatZar(l.subtotal_zar)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{formatZar(l.total_zar)}</td>
                <td style={{ textAlign: 'right' }}>
                  {editable && (
                    <>
                      <button className="ghost" style={{ padding: '4px 8px' }} onClick={() => setEditingId(String(l.ROWID))}>Edit</button>
                      <button
                        className="ghost"
                        style={{ padding: '4px 8px', color: 'var(--color-danger)' }}
                        onClick={async () => {
                          if (!confirm(`Delete this line? ${l.kind === 'time_entry' ? 'The time entry will be released back to unbilled.' :
                                       l.kind === 'disbursement' ? 'The disbursement will be released back to unbilled.' : ''}`.trim())) return;
                          await invoiceApi.deleteLine(invoiceId, l.ROWID);
                          onChange();
                        }}
                      >×</button>
                    </>
                  )}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      )}

      {adding && (
        <LineModal
          invoiceId={invoiceId}
          currency={currency}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); onChange(); }}
        />
      )}
      {editingId && (
        <LineModal
          invoiceId={invoiceId}
          existing={lines.find((l) => String(l.ROWID) === editingId)}
          currency={currency}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); onChange(); }}
        />
      )}
    </div>
  );
}

function LineModal({
  invoiceId, existing, currency, onClose, onSaved,
}: { invoiceId: number | string; existing?: InvoiceLine; currency: string; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<NewInvoiceLine>(() => ({
    description: existing?.description || '',
    quantity:    existing ? Number(existing.quantity) : 1,
    rate_zar:    existing ? Number(existing.rate_zar) : 0,
    kind:        existing?.kind || 'fixed_fee',
    unit:        existing?.unit || 'fixed',
    tax_percent: existing ? Number(existing.tax_percent) : 15,
    tax_rate_code: existing?.tax_rate_code || 'ZA-VAT-STD',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewSubtotal = useMemo(() => +(Number(form.quantity) * Number(form.rate_zar)).toFixed(2), [form.quantity, form.rate_zar]);
  const previewTax      = useMemo(() => +(previewSubtotal * (Number(form.tax_percent) / 100)).toFixed(2), [previewSubtotal, form.tax_percent]);
  const previewTotal    = useMemo(() => +(previewSubtotal + previewTax).toFixed(2), [previewSubtotal, previewTax]);

  const save = async () => {
    if (!form.description.trim())   { setError('Description is required.'); return; }
    if (!(Number(form.quantity) > 0)) { setError('Quantity must be > 0.'); return; }
    if (!(Number(form.rate_zar) > 0)) { setError('Rate must be > 0.'); return; }
    setSaving(true); setError(null);
    try {
      if (existing) {
        await invoiceApi.patchLine(invoiceId, existing.ROWID, form);
      } else {
        await invoiceApi.addLine(invoiceId, form);
      }
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card stack" style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0 }}>{existing ? 'Edit line' : 'Add manual line'}</h3>

        <div>
          <label>Description</label>
          <input autoFocus value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>

        <div className="row">
          <div style={{ width: 160 }}>
            <label>Kind</label>
            <Select
              value={form.kind || 'fixed_fee'}
              onChange={(v) => setForm({ ...form, kind: v as NewInvoiceLine['kind'] })}
              options={[
                { value: 'fixed_fee',  label: 'Fixed fee' },
                { value: 'adjustment', label: 'Adjustment' },
                { value: 'discount',   label: 'Discount' },
              ]}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label>Unit</label>
            <input value={form.unit || 'fixed'} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="hour, day, fixed, …" />
          </div>
        </div>

        <div className="row">
          <div style={{ flex: 1 }}>
            <label>Quantity</label>
            <input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} />
          </div>
          <div style={{ flex: 1 }}>
            <label>Rate ({currency})</label>
            <input type="number" step="0.01" value={form.rate_zar} onChange={(e) => setForm({ ...form, rate_zar: Number(e.target.value) })} />
          </div>
          <div style={{ width: 100 }}>
            <label>VAT %</label>
            <input type="number" step="0.01" value={form.tax_percent ?? 15} onChange={(e) => setForm({ ...form, tax_percent: Number(e.target.value) })} />
          </div>
        </div>

        <div className="card" style={{ background: 'var(--color-surface-2)' }}>
          <div className="row" style={{ justifyContent: 'space-between', fontSize: 13 }}>
            <span className="muted">Subtotal</span><strong>{formatZar(previewSubtotal)}</strong>
          </div>
          <div className="row" style={{ justifyContent: 'space-between', fontSize: 13 }}>
            <span className="muted">VAT</span><strong>{formatZar(previewTax)}</strong>
          </div>
          <div className="row" style={{ justifyContent: 'space-between', fontSize: 14, marginTop: 4 }}>
            <span>Line total</span><strong>{formatZar(previewTotal)}</strong>
          </div>
        </div>

        {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={saving}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : existing ? 'Save changes' : 'Add line'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Credit-note button (full / partial) -----------------------------------
function CreditNoteButton({
  invoice, lines, onCreated,
}: { invoice: InvoiceDetailT; lines: InvoiceLine[]; onCreated: (cn: { id: string | number; number: string }) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [mode, setMode]     = useState<'full' | 'partial'>('full');
  const [picked, setPicked] = useState<Record<string, number>>(() => Object.fromEntries(lines.map((l) => [String(l.ROWID), Number(l.quantity)])));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const body: Parameters<typeof invoiceApi.creditNote>[1] = { reason: reason || undefined };
      if (mode === 'partial') {
        body.lines = lines.map((l) => ({ line_id: l.ROWID, quantity: picked[String(l.ROWID)] || 0 }))
                          .filter((sel) => sel.quantity > 0);
        if (!body.lines.length) { setError('Pick at least one line to credit.'); setSaving(false); return; }
      }
      const r = await invoiceApi.creditNote(invoice.ROWID, body);
      onCreated({ id: r.id, number: r.number });
    } catch (e) { setError((e as Error).message); setSaving(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} title="Issue a credit note against this invoice">Credit note</button>
      {open && (
        <div style={backdrop} onClick={() => setOpen(false)}>
          <div className="card stack" style={{ ...modalCard, width: 640 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0 }}>Credit note against {invoice.number}</h3>
            <div className="row" style={{ gap: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, textTransform: 'none' }}>
                <input type="radio" checked={mode === 'full'} onChange={() => setMode('full')} style={{ width: 'auto' }} />
                Full credit
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, textTransform: 'none' }}>
                <input type="radio" checked={mode === 'partial'} onChange={() => setMode('partial')} style={{ width: 'auto' }} />
                Partial — pick lines & quantities
              </label>
            </div>

            {mode === 'partial' && (
              <div className="card" style={{ padding: 0, maxHeight: 280, overflowY: 'auto' }}>
                <table>
                  <thead>
                    <tr><th>Description</th><th style={{ textAlign: 'right' }}>Original qty</th><th style={{ width: 120 }}>Credit qty</th></tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => {
                      const slot = timeSlotLabel(l);
                      return (
                      <tr key={String(l.ROWID)}>
                        <td>
                          <div>{l.description}</div>
                          {slot && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{slot}</div>}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(l.quantity).toFixed(2)}</td>
                        <td>
                          <input
                            type="number" step="0.01" min="0" max={Number(l.quantity)}
                            value={picked[String(l.ROWID)] ?? 0}
                            onChange={(e) => setPicked({ ...picked, [String(l.ROWID)]: Number(e.target.value) })}
                          />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div>
              <label>Reason (optional, appears on the credit-note)</label>
              <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Hours over-billed on attendance of 12 March" />
            </div>

            {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
              <button className="primary" onClick={submit} disabled={saving}>
                {saving ? 'Issuing…' : 'Issue credit note'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---- Helpers ---------------------------------------------------------------
function Metric({ label, value, accent, danger }: { label: string; value: string; accent?: boolean; danger?: boolean }) {
  return (
    <div className="card">
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4,
                    color: danger ? 'var(--color-danger)' : accent ? 'var(--color-accent)' : 'var(--color-text)' }}>
        {value}
      </div>
    </div>
  );
}

function statusToneClass(s: string): string {
  if (s === 'paid')           return 'success';
  if (s === 'partially_paid') return 'info';
  if (s === 'issued')         return 'accent';
  if (s === 'overdue')        return 'danger';
  return '';
}

function formatZar(n: number | string) {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 }).format(Number(n) || 0);
}

const sectionHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 18px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-2)',
};
const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modalCard: React.CSSProperties = { width: 560, maxWidth: '90vw' };
